import "echarts-wordcloud";

import * as echarts from "echarts";
import { useLayoutEffect, useRef } from "react";

import { IAnalyzeState } from "../types";

interface IProps {
    wordCloudData: IAnalyzeState[] | undefined;
    category: string;
    isZoomed?: boolean;
}

const useWordCloudZoom = ({ wordCloudData, category, isZoomed = false }: IProps) => {
    const wordCloudChart = useRef(null);

    useLayoutEffect(() => {
        if (!wordCloudData) return;

        // 等待 DOM 元素準備好
        const initChart = () => {
            if (!wordCloudChart.current) {
                setTimeout(initChart, 10);
                return;
            }

            let chart: echarts.ECharts | null = null;

            try {
                chart = echarts.init(wordCloudChart.current);
            } catch (error) {
                return;
            }

            if (!chart) return;

            const option = {
                title: {
                    text: category,
                    left: "center",
                    top: "5%",
                    textStyle: {
                        fontSize: isZoomed ? 24 : 16,
                        fontWeight: "bold",
                    },
                },
                tooltip: {
                    show: true,
                    textStyle: {
                        fontSize: isZoomed ? 16 : 14,
                    },
                    formatter: (params: { name: string; value: number }) => {
                        return `${params.name}<br/>頻率: ${params.value}`;
                    },
                },
                series: [
                    {
                        type: "wordCloud",
                        shape: "circle",
                        keepAspect: false,
                        left: "center",
                        top: "center",
                        width: isZoomed ? "85%" : "90%",
                        height: isZoomed ? "75%" : "70%",
                        right: null,
                        bottom: null,
                        sizeRange: isZoomed ? [16, 72] : [12, 52],
                        rotationRange: [-90, 90],
                        rotationStep: 45,
                        gridSize: isZoomed ? 6 : 8,
                        drawOutOfBound: false,
                        shrinkToFit: true,
                        layoutAnimation: true,
                        textStyle: {
                            fontFamily: "sans-serif",
                            fontWeight: "bold",
                            color: function () {
                                return (
                                    "rgb(" +
                                    [
                                        Math.round(Math.random() * 160),
                                        Math.round(Math.random() * 160),
                                        Math.round(Math.random() * 160),
                                    ].join(",") +
                                    ")"
                                );
                            },
                        },
                        emphasis: {
                            focus: "self",
                            textStyle: {
                                textShadowBlur: isZoomed ? 15 : 10,
                                textShadowColor: "#333",
                            },
                        },
                        data: wordCloudData || [],
                    },
                ],
            };

            try {
                chart.setOption(option);
            } catch (error) {
                return;
            }

            // 響應式調整
            const handleResize = () => {
                if (chart && !chart.isDisposed()) {
                    try {
                        chart.resize();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };

            window.addEventListener("resize", handleResize);

            return () => {
                window.removeEventListener("resize", handleResize);
                if (chart && !chart.isDisposed()) {
                    try {
                        chart.dispose();
                    } catch (error) {
                        // 靜默處理錯誤
                    }
                }
            };
        };

        initChart();
    }, [wordCloudData, category, isZoomed]);

    return wordCloudChart;
};

export default useWordCloudZoom;
